import { BiblePicture } from './BiblePicture'
import { createPortal } from 'react-dom'
import { useLayoutEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { updateRoute, useRouteState } from '../routeState'
import type { StoryProject } from '../types'
import { WorkspaceDialog } from './WorkspaceDialog'

export const openBiblePicture = (targetId: string, pictureId: string) =>
  updateRoute({ 'picture-entry': targetId, picture: pictureId })

export function BibleImageViewer({ project }: { project: StoryProject | null }) {
  const [targetId] = useRouteState<string | null>('picture-entry', null)
  const [pictureId] = useRouteState<string | null>('picture', null)
  const character = project?.bible?.characters.find(item => 'character:' + item.id === targetId)
  const entry = project?.bible?.entries?.find(item => 'entry:' + item.id === targetId)
  const pictures = (character || entry)?.images || []
  const index = pictures.findIndex(picture => picture.id === pictureId)
  const picture = pictures[index]
  const title = character?.name || entry?.title || 'Bible pictures'
  const touch = useRef<{ x: number; y: number } | null>(null)
  const close = () => updateRoute({ picture: null, 'picture-entry': null })
  const move = (step: number) => {
    // Router rendering can trail a fast second key/click. The URL already holds
    // the last navigation, so every press advances from that picture.
    const currentIndex = pictures.findIndex(item => item.id === new URLSearchParams(window.location.search).get('picture'))
    if (pictures.length > 1) openBiblePicture(targetId!, pictures[(Math.max(currentIndex, 0) + step + pictures.length) % pictures.length].id)
  }
  useLayoutEffect(() => {
    if (!pictureId || !targetId) return
    const key = (event: KeyboardEvent) => {
      if (!document.activeElement?.closest('.bible-picture-viewer')) return
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault(); event.stopPropagation(); move(event.key === 'ArrowRight' ? 1 : -1)
      }
    }
    document.addEventListener('keydown', key)
    return () => document.removeEventListener('keydown', key)
  }, [pictureId, targetId, pictures])
  if (!pictureId || !targetId) return null
  return createPortal(<WorkspaceDialog className="bible-picture-viewer" title={title} eyebrow="PICTURES & VISUAL REFERENCES" initialFocus="close" onClose={close}>
    {picture ? <>
      <div className="bible-picture-stage" onTouchStart={event => {
        touch.current = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null
      }} onTouchCancel={() => { touch.current = null }} onTouchEnd={event => {
        const start = touch.current; touch.current = null
        if (!start || event.touches.length || !event.changedTouches[0]) return
        const dx = event.changedTouches[0].clientX - start.x, dy = event.changedTouches[0].clientY - start.y
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) { event.preventDefault(); move(dx < 0 ? 1 : -1) }
      }}>
        <BiblePicture picture={picture} alt={picture.alt || picture.name} draggable={false} />
        {pictures.length > 1 && <>
          <button type="button" className="picture-previous" aria-label="Previous picture" onClick={() => move(-1)}><ChevronLeft size={24} /></button>
          <button type="button" className="picture-next" aria-label="Next picture" onClick={() => move(1)}><ChevronRight size={24} /></button>
        </>}
      </div>
      <div className="bible-picture-caption" aria-live="polite"><span>{index + 1} / {pictures.length}</span><p>{picture.alt || picture.name}</p></div>
      {pictures.length > 1 && <nav className="bible-picture-thumbnails" aria-label="Choose a picture">
        {pictures.map((item, position) => <button key={item.id} type="button" aria-label={'Picture ' + (position + 1) + ': ' + (item.alt || item.name)} aria-current={item.id === pictureId ? 'true' : undefined}
          onClick={() => openBiblePicture(targetId, item.id)}><BiblePicture picture={item} alt="" loading="lazy" /></button>)}
      </nav>}
    </> : <p className="empty-message">{project ? 'This picture is no longer in the Bible.' : 'Opening your picture…'}</p>}
  </WorkspaceDialog>, document.body)
}
